import { useEffect, useRef } from "react";

// One per colour; must match the length of $keyword-highlight-colors in main.scss
const HIGHLIGHT_REGISTRY_KEYS = [
    "keyword-highlight-0",
    "keyword-highlight-1",
    "keyword-highlight-2",
    "keyword-highlight-3",
    "keyword-highlight-4",
];

const SKIP_HTML_ELEMENTS = [
    "script",
    "style",
    "noscript",
    "iframe",
    "canvas",
    "svg",
];
// data-disable-highlight opts an element and everything inside it out (e.g. field labels)
const SKIP_SELECTOR = [...SKIP_HTML_ELEMENTS, "[data-disable-highlight]"].join(
    ",",
);

const OBSERVER_CONFIG = {
    childList: true,
    characterData: true,
    subtree: true,
};

const isHighlightAPISupported =
    "Highlight" in window && Boolean(window.CSS?.highlights);

const ACCENT_MARKS = /\p{M}/gu;

const applyCaseAndAccentOptions = (
    text,
    { isCaseSensitive, isAccentSensitive },
) => {
    const casedText = isCaseSensitive ? text : text.toLowerCase();
    return isAccentSensitive
        ? casedText.normalize("NFC")
        : casedText.normalize("NFD").replace(ACCENT_MARKS, "");
};

function keywordsToSearchTerms(keywords, options) {
    const terms = (keywords ?? [])
        .map((keyword) => keyword?.trim())
        .filter(Boolean)
        .map((keyword) => applyCaseAndAccentOptions(keyword, options));
    return [...new Set(terms)];
}

const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const isLetterOrDigit = (character) => LETTER_OR_DIGIT.test(character ?? "");

function findWholeWordStarts(text, term) {
    const starts = [];
    for (
        let start = text.indexOf(term);
        start !== -1;
        start = text.indexOf(term, start + 1)
    ) {
        const isInsideWord =
            isLetterOrDigit(text[start - 1]) ||
            isLetterOrDigit(text[start + term.length]);
        if (!isInsideWord) starts.push(start);
    }
    return starts;
}

function findNonOverlappingMatches(text, terms) {
    const matches = [];
    for (const { term, termIndex } of terms) {
        for (const start of findWholeWordStarts(text, term)) {
            const end = start + term.length;
            const overlapsLongerMatch = matches.some(
                (match) => start < match.end && end > match.start,
            );
            if (!overlapsLongerMatch) matches.push({ start, end, termIndex });
        }
    }
    return matches;
}

function getTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
            node.parentElement?.closest(SKIP_SELECTOR)
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT,
    });

    const nodes = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        nodes.push(node);
    }
    return nodes;
}

const createTextRange = (node, start, end) => {
    const range = new Range();
    range.setStart(node, start);
    range.setEnd(node, end);
    return range;
};

function buildHighlights(root, terms, options) {
    const highlights = HIGHLIGHT_REGISTRY_KEYS.map(
        () => new window.Highlight(),
    );
    const termsLongestFirst = terms
        .map((term, termIndex) => ({ term, termIndex }))
        .sort((a, b) => b.term.length - a.term.length);

    for (const node of getTextNodes(root)) {
        const text = applyCaseAndAccentOptions(node.data, options);
        // Normalizing can change the length (e.g. "İ"), which would shift the match positions
        if (text.length !== node.data.length) continue;

        const matches = findNonOverlappingMatches(text, termsLongestFirst);
        for (const { start, end, termIndex } of matches) {
            const colour = highlights[termIndex % highlights.length];
            colour.add(createTextRange(node, start, end));
        }
    }
    return highlights;
}

function removeHighlights() {
    if (!isHighlightAPISupported) return;
    HIGHLIGHT_REGISTRY_KEYS.forEach((key) => CSS.highlights.delete(key));
}

function drawHighlights(root, { terms, options }) {
    if (!isHighlightAPISupported || !root) return;

    if (terms.length) {
        buildHighlights(root, terms, options).forEach((highlight, index) =>
            CSS.highlights.set(HIGHLIGHT_REGISTRY_KEYS[index], highlight),
        );
    } else {
        removeHighlights();
    }
}

// Module-level so the default is the same object every render and the effect runs once
const BODY_REF = { current: document.body };

// Highlights use global CSS.highlights keys, so only one instance should be mounted at a time.
// No targetRef watches the whole page. The ref's element must exist when the component mounts.
function useKeywordHighlight(targetRef = BODY_REF, config = OBSERVER_CONFIG) {
    const searchRef = useRef({ terms: [], options: {} });
    const observerRef = useRef(null);

    // After cleanup() nothing is drawn again until the component remounts
    const cleanup = () => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        removeHighlights();
    };

    useEffect(() => {
        const targetNode = targetRef.current;
        if (!targetNode || !isHighlightAPISupported) return;

        const observer = new MutationObserver(() =>
            drawHighlights(targetNode, searchRef.current),
        );
        observer.observe(targetNode, config);
        observerRef.current = observer;
        drawHighlights(targetNode, searchRef.current);

        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetRef]);

    const highlight = (
        keywords,
        { isCaseSensitive = false, isAccentSensitive = false } = {},
    ) => {
        const options = { isCaseSensitive, isAccentSensitive };
        const search = {
            terms: keywordsToSearchTerms(keywords, options),
            options,
        };
        if (JSON.stringify(search) === JSON.stringify(searchRef.current))
            return;

        searchRef.current = search;
        if (observerRef.current) drawHighlights(targetRef.current, search);
    };

    const clearHighlight = () => highlight([]);

    return { highlight, clearHighlight, cleanup };
}
export { useKeywordHighlight };
